import React, { useEffect, useCallback, useState, useRef, useMemo } from "react";
import { useTranslation, Trans } from "react-i18next";
import { useOutletContext } from "react-router-dom";
import {
  Button,
  ExpandSingle,
  InfoSmallIcon,
  Label,
  Textarea,
  Tooltip,
  Checkbox,
  InputHours,
} from "@activecollab/components";
import { useFormik } from "formik";
import PropTypes from "prop-types";
import * as yup from "yup";
import { useAvailableJobTypes } from "~/repository/useAvailableJobTypes.js";
import Angie from "~/utils/angie.jsx";
import { determinePrecision, timeStringToFloat } from "~/utils/date.jsx";
import { UserAvailableJobTypesRealtime } from "../JobTypes/UserAvailableJobTypesRealTime.js";
import SelectContainer from "../SelectContainer/SelectContainer.js";

export const inputTimeName = "value";
export const descriptionName = "summary";
export const jobTypeName = "job_type_id";
export const checkboxBillableName = "billable_status";
export const checkboxKeepInStopwatchName = "keep_in_stopwatch";
export const userId = "user_id";

const validationSchema = yup.object().shape({
  [inputTimeName]: yup
    .string()
    .test("valid-decimal", "invalid-decimal", (val) => {
      return val && Number(val.replace(/[:,.]/g, "")) > 0;
    })
    .required(),
  [descriptionName]: yup.string(),
  [jobTypeName]: yup.number(),
  [checkboxBillableName]: yup.boolean(),
  [checkboxKeepInStopwatchName]: yup.boolean(),
  [userId]: yup.number(),
});

const TimeRecordFormBody = ({
  jobTypes,
  defaultJobTypeId,
  defaultUserId,
  defaultUserName,
  timeRecordValue,
  isBillable,
  keepInStopwatch,
  canSeeBillable,
  onCancel,
  onSubmit,
  onKeepInStopwatchChange,
  users,
  stopwatchRoundingInterval,
  showRoundingInfo,
  loggedUser,
  defaultShowDescription,
  onToggleShowDescription,
  descriptionValue,
  source,
}) => {
  const { t } = useTranslation();
  const [showDescription, setShowDescription] = useState(defaultShowDescription);
  const inputRef = useRef(null);
  const outletData = useOutletContext();
  const outletUserId = +outletData?.userId;
  const { data } = useAvailableJobTypes();
  const formikRef = useRef(null);
  const handleSubmit = useCallback(
    (values) => {
      const modified = {
        ...values,
      };

      if (!users.length) {
        delete modified[userId];
      }

      if (!canSeeBillable) {
        delete modified[checkboxBillableName];
      }

      delete modified[checkboxKeepInStopwatchName];

      const rawValue = inputRef.current?.value;

      const getValueFormat = (value) => {
        if (value?.includes(",") || value?.includes(".")) return "decimal";
        if (value?.includes(":")) return "colon";
        return "whole-number";
      };

      Angie.logger?.info("Time record added", {
        raw_value: rawValue,
        value: timeStringToFloat(modified.value),
        format: getValueFormat(rawValue),
        precision: determinePrecision(rawValue),
        time_record_source: source,
      });

      modified.value = timeStringToFloat(modified.value);

      typeof onSubmit === "function" && onSubmit(modified, values[checkboxKeepInStopwatchName]);
    },
    [source, onSubmit, users, canSeeBillable]
  );

  const initialValues = useMemo(() => {
    return {
      [inputTimeName]: timeRecordValue,
      [jobTypeName]: defaultJobTypeId,
      [checkboxBillableName]: isBillable,
      [checkboxKeepInStopwatchName]: keepInStopwatch,
      [userId]: defaultUserId,
      [descriptionName]: descriptionValue,
    };
  }, [defaultJobTypeId, defaultUserId, descriptionValue, isBillable, keepInStopwatch, timeRecordValue]);

  const formik = useFormik({
    innerRef: formikRef,
    initialValues: initialValues,
    onSubmit: handleSubmit,
    validationSchema: validationSchema,
    validateOnMount: true,
    enableReinitialize: true,
  });

  const currentUserId = formik.values[userId] ? formik.values[userId] : outletUserId ? outletUserId : loggedUser.id;

  const availableJobTypes = data ? jobTypes.filter((jobType) => data[currentUserId]?.includes(jobType.id)) : [];
  const selectedJob =
    availableJobTypes.find((jobType) => jobType.id === formik.values[jobTypeName]) ?? availableJobTypes[0];

  useEffect(() => {
    if (selectedJob && selectedJob.id !== formik.values[jobTypeName]) {
      formik.setFieldValue(jobTypeName, selectedJob.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedJob]);

  const handleChangeDescription = useCallback(
    (e) => {
      if (e && e.target) {
        const value = e.target.value;
        const name = e.target.name;
        formik.setFieldValue(name, value);
      }
    },
    [formik]
  );

  const handleToggleBillableStatus = useCallback(() => {
    const prevValue = formik.values.billable_status;
    formik.setFieldValue("billable_status", !prevValue);
  }, [formik]);

  const handleToggleKeepInStopwatch = useCallback(() => {
    const nextValue = !formik.values[checkboxKeepInStopwatchName];
    formik.setFieldValue(checkboxKeepInStopwatchName, nextValue);
    typeof onKeepInStopwatchChange === "function" && onKeepInStopwatchChange(nextValue);
  }, [formik, onKeepInStopwatchChange]);

  const handleChangeTimeInput = useCallback(
    (e) => {
      formik.setFieldValue(inputTimeName, e.target.value);
    },
    [formik]
  );

  const handleJobType = useCallback(
    (id) => {
      formik.setFieldValue(jobTypeName, id ?? defaultJobTypeId);
    },
    [defaultJobTypeId, formik]
  );

  const handleSelectUser = useCallback(
    (id) => {
      formik.setFieldValue(userId, id || defaultUserId);
    },
    [defaultUserId, formik]
  );

  const handleDescriptionToggle = useCallback(() => {
    const value = !showDescription;
    setShowDescription(value);
    typeof onToggleShowDescription === "function" && onToggleShowDescription(value);
  }, [setShowDescription, showDescription, onToggleShowDescription]);

  const stopwatchText = useMemo(() => {
    return (
      <>
        <div>
          <Trans>
            Time records are set to be rounded to {{ stopwatchRoundingInterval }} min interval by the account owner.
          </Trans>
        </div>
        {loggedUser.class === "Owner" ? <div>{t("Check System Settings.")}</div> : null}
      </>
    );
  }, [loggedUser, stopwatchRoundingInterval, t]);

  const handleChange = (value) => {
    formik.setFieldValue(inputTimeName, value);
  };

  const onEnterKeyPress = async (val) => {
    await formik.setFieldValue(inputTimeName, timeStringToFloat(val));
    await formik.submitForm();
  };

  return (
    <>
      <form
        className="time_record_form_body tw-w-full"
        onSubmit={formik.handleSubmit}
      >
        <div className="tw-flex tw-gap-1 sm:tw-gap-0 tw-flex-col sm:tw-flex-row sm:tw-items-end">
          <div className="tw-relative tw-w-full sm:tw-w-36 tw-mr-4">
            {showRoundingInfo ? (
              <Tooltip
                title={stopwatchText}
                isLight
              >
                <InfoSmallIcon className="stopwatch-form-info" />
              </Tooltip>
            ) : null}
            <InputHours
              size="biggest"
              ref={inputRef}
              name={inputTimeName}
              id="c-input"
              className="tw-w-full"
              onSave={handleChangeTimeInput}
              value={formik.values[inputTimeName]}
              autoFocus
              allowEmptyValue
              onChange={handleChange}
              onEnterKeyPress={onEnterKeyPress}
              invalid={formik.dirty && formik.errors[inputTimeName]}
            />
          </div>
          {users.length ? (
            <span className="tw-m-0 sm:tw-mr-4">
              <Label
                size="small"
                className="tw-mb-0.5"
              >
                {t("User")}
              </Label>
              <SelectContainer
                options={users}
                selected={formik.values.user_id}
                onChange={handleSelectUser}
                placeholder={t("Choose a User")}
                className="tw-w-full sm:tw-w-36"
                defaultSelectedName={defaultUserName}
              />
            </span>
          ) : null}
          {availableJobTypes.length > 1 ? (
            <span className="tw-m-0 sm:tw-mr-5">
              <Label
                size="small"
                className="tw-mb-0.5"
              >
                {t("Job Type")}
              </Label>
              <SelectContainer
                options={availableJobTypes}
                selected={selectedJob.id}
                onChange={handleJobType}
                placeholder={t("Choose a Job Type")}
                className="tw-w-full sm:tw-w-36"
              />
            </span>
          ) : null}
          {canSeeBillable ? (
            <span className="tw-flex flex-row tw-mr-5 tw-mt-6">
              <Checkbox
                id="time_record_dialog_checkbox"
                name={checkboxBillableName}
                checked={formik.values[checkboxBillableName]}
                onChange={handleToggleBillableStatus}
                className="tw-mr-2"
              />
              <Label
                weight="regular"
                className="tw-cursor-pointer"
                htmlFor="time_record_dialog_checkbox"
              >
                {t("Billable")}
              </Label>
            </span>
          ) : null}
        </div>
        <div
          className="tw-cursor-pointer tw-inline-flex tw-items-center tw-mt-4 description_trigger"
          onClick={handleDescriptionToggle}
        >
          <ExpandSingle expanded={showDescription} />
          <span>{t("Description")}</span>
        </div>
        {showDescription ? (
          <div>
            <Textarea
              name={descriptionName}
              defaultValue={formik.values[descriptionName]}
              onChange={handleChangeDescription}
              className="summary_time_record time_record_summary_textarea tw-w-full sm:tw-w-[360px]"
            />
          </div>
        ) : null}
        {source === "built_in_timer" ? (
          <div className="tw-flex flex-row tw-mt-6 tw-items-center">
            <Checkbox
              id="keep_in_stopwatch_checkbox"
              name={checkboxKeepInStopwatchName}
              checked={formik.values[checkboxKeepInStopwatchName]}
              onChange={handleToggleKeepInStopwatch}
              className="tw-mr-2"
            />
            <Label
              weight="regular"
              className="tw-cursor-pointer"
              htmlFor="keep_in_stopwatch_checkbox"
            >
              {t("Reset and keep the stopwatch")}
            </Label>
          </div>
        ) : null}
        <div className="tw-flex tw-mt-6">
          <Button
            variant="primary"
            disabled={!formik.isValid || formik.isSubmitting}
            type="submit"
            className="tw-mr-3"
          >
            {t("Add Time Record")}
          </Button>
          <Button
            variant="secondary"
            onClick={onCancel}
            type="button"
          >
            {t("Cancel")}
          </Button>
        </div>
      </form>
      <UserAvailableJobTypesRealtime />
    </>
  );
};

TimeRecordFormBody.propTypes = {
  loggedUser: PropTypes.object.isRequired,
  jobTypes: PropTypes.array,
  users: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      avatarUrl: PropTypes.string,
    })
  ),
  defaultJobTypeId: PropTypes.number,
  stopwatchRoundingInterval: PropTypes.number,
  defaultUserId: PropTypes.number,
  timeRecordValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isBillable: PropTypes.bool,
  keepInStopwatch: PropTypes.bool,
  canSeeBillable: PropTypes.bool,
  showRoundingInfo: PropTypes.bool,
  onCancel: PropTypes.func,
  onSubmit: PropTypes.func,
  onKeepInStopwatchChange: PropTypes.func,
  onToggleShowDescription: PropTypes.func,
  defaultShowDescription: PropTypes.bool,
  descriptionValue: PropTypes.string,
  isSubmitInProgress: PropTypes.bool,
  source: PropTypes.string,
  defaultUserName: PropTypes.string,
};

TimeRecordFormBody.defaultProps = {
  jobTypes: [],
  users: [],
  defaultJobTypeId: 1,
  defaultUserId: 0,
  stopwatchRoundingInterval: 0,
  timeRecordValue: "",
  isBillable: false,
  showRoundingInfo: false,
  canSeeBillable: true,
  defaultShowDescription: false,
  descriptionValue: "",
  isSubmitInProgress: false,
};

export default TimeRecordFormBody;
